import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Play, ShoppingCart, ExternalLink, Music, Loader2, Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState('digital');

  useEffect(() => {
    const fetchAlbum = async () => {
      try {
        const response = await axios.get(`${API}/albums/${id}`);
        setAlbum(response.data);
      } catch (error) {
        console.error('Error fetching album:', error);
        toast.error(t('Album not found', 'Álbum no encontrado'));
      } finally {
        setLoading(false);
      }
    };
    fetchAlbum();
  }, [id, t]);

  const handleAddToCart = (goToCart = false) => {
    if (!album) return;
    addItem(album, variant, 1);
    toast.success(
      t(`Added to cart: ${album.title_es || album.title}`, `Añadido al carrito: ${album.title_es || album.title}`)
    );
    if (goToCart) navigate('/cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pt-32 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-gold-500 animate-spin" />
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen bg-cream pt-32" data-testid="product-not-found">
        <div className="container mx-auto px-4 md:px-8 text-center py-20">
          <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="font-playfair text-3xl font-bold text-charcoal mb-4">
            {t('Product Not Found', 'Producto No Encontrado')}
          </h1>
          <p className="font-lato text-gray-600 mb-8">
            {t('The album or single you\'re looking for doesn\'t exist.', 'El álbum o sencillo que buscas no existe.')}
          </p>
          <Link to="/music">
            <Button className="bg-gold-500 hover:bg-gold-600 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('Back to Music', 'Volver a Música')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isAlbum = album.price >= 5;
  const title = language === 'en' ? album.title : album.title_es;
  const description = language === 'en' ? album.description : album.description_es;

  return (
    <div className="min-h-screen bg-cream" data-testid="product-detail-page">
      {/* Back Button */}
      <div className="pt-24 pb-4 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-8">
          <Link 
            to="/music" 
            className="inline-flex items-center gap-2 font-lato text-gray-600 hover:text-gold-600 transition-colors"
            data-testid="back-to-music"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('Back to Music Store', 'Volver a la Tienda')}
          </Link>
        </div>
      </div>

      {/* Product Detail */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Image */}
            <div className="relative">
              <div className="sticky top-32">
                <div className="relative aspect-square bg-white rounded-2xl shadow-xl overflow-hidden">
                  <img 
                    src={album.image_url} 
                    alt={title}
                    className="w-full h-full object-cover"
                    data-testid="product-image"
                  />
                  {album.featured && (
                    <span className="absolute top-4 left-4 bg-gold-500 text-white font-lato text-xs uppercase tracking-wider px-4 py-2 rounded-full">
                      {t('Featured', 'Destacado')}
                    </span>
                  )}
                </div>
                
                {/* Play Button if YouTube available */}
                {album.youtube_playlist_url && (
                  <a 
                    href={album.youtube_playlist_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-4 right-4 w-16 h-16 bg-gold-500 hover:bg-gold-400 rounded-full flex items-center justify-center shadow-xl transition-colors"
                    data-testid="play-youtube"
                  >
                    <Play className="w-8 h-8 text-white ml-1" />
                  </a>
                )}
              </div>
            </div>

            {/* Info */}
            <div>
              <div className="mb-6">
                <span className="font-lato text-gold-600 text-sm uppercase tracking-widest">
                  {isAlbum ? t('Album', 'Álbum') : t('Single', 'Sencillo')} • {album.year}
                </span>
              </div>
              
              <h1 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-4" data-testid="product-title">
                {title}
              </h1>
              
              <p className="font-lato text-xl text-gray-600 mb-8 leading-relaxed" data-testid="product-description">
                {description}
              </p>

              {/* Price */}
              <div className="flex items-baseline gap-4 mb-8">
                <span className="font-playfair text-5xl font-bold text-gold-600" data-testid="product-price">
                  ${album.price.toFixed(2)}
                </span>
                <span className="font-lato text-gray-500">USD</span>
              </div>

              {/* Songs List */}
              {album.songs && album.songs.length > 0 && (
                <div className="mb-8 p-6 bg-white rounded-xl shadow-md">
                  <h3 className="font-playfair text-lg font-semibold text-charcoal mb-4 flex items-center gap-2">
                    <Music className="w-5 h-5 text-gold-500" />
                    {t('Tracks Included', 'Canciones Incluidas')}
                  </h3>
                  <ul className="space-y-2">
                    {album.songs.map((song, index) => (
                      <li key={index} className="font-lato text-gray-600 flex items-center gap-3">
                        <span className="w-6 h-6 bg-gold-100 rounded-full flex items-center justify-center text-gold-600 text-sm font-semibold">
                          {index + 1}
                        </span>
                        {song}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Format / Variant selector (only albums with a physical version) */}
              {album.has_physical && (
                <div className="mb-6" data-testid="variant-selector">
                  <p className="font-lato text-xs text-gray-500 uppercase tracking-wider mb-3">
                    {t('Choose format', 'Elige formato')}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setVariant('digital')}
                      className={`text-left p-4 border-2 rounded-md transition-all ${
                        variant === 'digital'
                          ? 'border-gold-500 bg-gold-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gold-300'
                      }`}
                      data-testid="variant-digital"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-playfair text-lg font-bold text-charcoal">
                          {t('Digital', 'Digital')}
                        </span>
                        <span className="font-lato text-sm font-semibold text-gold-600">
                          ${album.price.toFixed(2)}
                        </span>
                      </div>
                      <p className="font-lato text-xs text-gray-500">
                        {t('Instant download', 'Descarga instantánea')}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setVariant('physical')}
                      className={`text-left p-4 border-2 rounded-md transition-all ${
                        variant === 'physical'
                          ? 'border-gold-500 bg-gold-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gold-300'
                      }`}
                      data-testid="variant-physical"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-playfair text-lg font-bold text-charcoal">
                          {t('Physical CD', 'CD Físico')}
                        </span>
                        <span className="font-lato text-sm font-semibold text-gold-600">
                          ${album.price.toFixed(2)}
                        </span>
                      </div>
                      <p className="font-lato text-xs text-gray-500">
                        {t(
                          `+ $${(album.shipping_fee ?? 4.95).toFixed(2)} shipping · 5–10 business days`,
                          `+ $${(album.shipping_fee ?? 4.95).toFixed(2)} envío · 5–10 días hábiles`
                        )}
                      </p>
                    </button>
                  </div>
                  <p className="font-lato text-xs text-gray-500 mt-3">
                    {t(
                      'Sales tax calculated at checkout based on your address.',
                      'Impuesto sobre la venta calculado al pagar según tu dirección.'
                    )}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Button
                  onClick={() => handleAddToCart(false)}
                  className="flex-1 bg-gold-500 hover:bg-gold-600 text-white font-lato uppercase tracking-wider py-6 text-lg"
                  data-testid="add-to-cart-button"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {album.has_physical && variant === 'physical'
                    ? t(
                        `Add to Cart – $${album.price.toFixed(2)}`,
                        `Añadir al Carrito – $${album.price.toFixed(2)}`
                      )
                    : t(
                        `Add to Cart – $${album.price.toFixed(2)}`,
                        `Añadir al Carrito – $${album.price.toFixed(2)}`
                      )}
                </Button>
                <Button
                  onClick={() => handleAddToCart(true)}
                  variant="outline"
                  className="flex-1 border-gold-500 text-gold-600 hover:bg-gold-50 font-lato uppercase tracking-wider py-6 text-lg"
                  data-testid="buy-now-button"
                >
                  {t('Buy Now', 'Comprar Ahora')}
                </Button>
              </div>

              {/* External Links */}
              <div className="flex flex-wrap gap-4">
                {album.spotify_url && album.spotify_url.startsWith('https://') && (
                  <a 
                    href={album.spotify_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1DB954] text-white rounded-full font-lato text-sm hover:opacity-90 transition-opacity"
                    data-testid="spotify-link"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                    </svg>
                    {t('Listen on Spotify', 'Escuchar en Spotify')}
                  </a>
                )}
                {album.youtube_playlist_url && (
                  <a 
                    href={album.youtube_playlist_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full font-lato text-sm hover:opacity-90 transition-opacity"
                    data-testid="youtube-link"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    {t('Watch on YouTube', 'Ver en YouTube')}
                  </a>
                )}
              </div>

              {/* Scripture */}
              <div className="mt-12 p-6 bg-white rounded-xl border border-gold-200">
                <Heart className="w-6 h-6 text-gold-500 mb-4" />
                <blockquote className="font-playfair text-lg text-charcoal italic mb-2">
                  {t(
                    '"Sing to the Lord a new song, for he has done marvelous things."',
                    '"Cantad a Jehová cántico nuevo, porque ha hecho maravillas."'
                  )}
                </blockquote>
                <p className="font-lato text-gold-600 text-sm uppercase tracking-wider">
                  — {t('Psalm 98:1', 'Salmo 98:1')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products CTA */}
      <section className="py-12 bg-charcoal">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h3 className="font-playfair text-2xl font-bold text-white mb-4">
            {t('Explore More Music', 'Explora Más Música')}
          </h3>
          <p className="font-lato text-gray-400 mb-6">
            {t('Discover all of Sarah Capeles\' albums and singles.', 'Descubre todos los álbumes y sencillos de Sarah Capeles.')}
          </p>
          <Link to="/music">
            <Button className="bg-gold-500 hover:bg-gold-600 text-white font-lato uppercase tracking-wider px-8">
              {t('View All Music', 'Ver Toda la Música')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
